/**
 * Calendar arithmetic on whole days.
 *
 * A day is the number of days since 1970-01-01 (a Thursday), counted on the UTC calendar so that daylight saving
 * time and the user's time zone can never move a date. Everything the planner schedules is a whole day, so ISO
 * `YYYY-MM-DD` strings are converted once on the way in and back on the way out and all comparisons, distances and
 * weekday counts in between are plain integer arithmetic.
 */

const MS_PER_DAY = 86400000;

const pad = (value, length) => String(value).padStart(length, "0");

const mod = (value, divisor) => ((value % divisor) + divisor) % divisor;

/** Day number of an ISO date `YYYY-MM-DD`; a longer ISO date-time is read for its date part only. */
export function isoToDay(iso) {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  const date = Number(iso.slice(8, 10));
  return Math.round(Date.UTC(year, month - 1, date) / MS_PER_DAY);
}

/** ISO date `YYYY-MM-DD` of a day number. */
export function dayToIso(day) {
  const date = new Date(day * MS_PER_DAY);
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1, 2)}-${pad(date.getUTCDate(), 2)}`;
}

/** The calendar day of a moment in the user's own time zone, as a day number. */
export function todayDay(now = new Date()) {
  return Math.round(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / MS_PER_DAY);
}

/** Day of the week, 0 = Sunday ... 6 = Saturday (the numbering of `Date#getDay`). */
export function dayOfWeek(day) {
  return mod(day + 4, 7);
}

export function isWeekendDay(day) {
  const dow = dayOfWeek(day);
  return dow === 0 || dow === 6;
}

/** The Monday on or before a day. */
export function mondayOnOrBefore(day) {
  return day - mod(day + 3, 7);
}

/**
 * Number of weekdays (Monday to Friday) from a fixed Monday before the epoch up to and including `day`.
 * Only differences of this are meaningful: see `countWeekdays`.
 */
function weekdaysThrough(day) {
  const sinceMonday = day + 3;
  return 5 * Math.floor(sinceMonday / 7) + Math.min(mod(sinceMonday, 7) + 1, 5);
}

/** Weekdays in the closed range `startDay`..`endDay`, in constant time. */
export function countWeekdays(startDay, endDay) {
  return endDay < startDay ? 0 : weekdaysThrough(endDay) - weekdaysThrough(startDay - 1);
}

/** Days in the closed range `startDay`..`endDay`. */
export function countDays(startDay, endDay) {
  return endDay < startDay ? 0 : endDay - startDay + 1;
}

/** Days an allocation counts in a closed range: every day when weekends are included, otherwise weekdays only. */
export function countAllocationDays(startDay, endDay, includeWeekends) {
  return includeWeekends ? countDays(startDay, endDay) : countWeekdays(startDay, endDay);
}

/** Splits a day into the calendar year, month (1-12) and day of the month. */
export function toCivil(day) {
  const date = new Date(day * MS_PER_DAY);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, date: date.getUTCDate() };
}

export function fromCivil(year, month, date) {
  return Math.round(Date.UTC(year, month - 1, date) / MS_PER_DAY);
}

export function startOfMonth(day) {
  const { year, month } = toCivil(day);
  return fromCivil(year, month, 1);
}

export function endOfMonth(day) {
  const { year, month } = toCivil(day);
  return fromCivil(year, month + 1, 1) - 1;
}

/** The same day of the month a number of months later, or the last day of that month when it has no such day. */
export function addMonths(day, months) {
  const { year, month, date } = toCivil(day);
  const first = fromCivil(year, month + months, 1);
  return Math.min(first + date - 1, endOfMonth(first));
}

/** The longest stretch of days Resource Insights is asked about, which is what the server accepts (ten years). */
export const MAX_PERIOD_DAYS = 3660;

/**
 * The period a summary pill refers to, relative to a day. Weeks run Monday to Sunday.
 * `last_week` | `this_week` | `next_week` | `last_month` | `this_month` | `next_month`, `custom`, which is the
 * `custom` range `{ startDay, endDay }` the user picked, or `overall`, which is the whole life of the `project` the
 * planner is limited to, `{ startDay, endDay }`, cut to `MAX_PERIOD_DAYS`. Anything else is this week, and so are
 * `custom` and `overall` while their days are not known.
 */
export function periodRange(period, today, { custom = null, project = null } = {}) {
  const weekStart = mondayOnOrBefore(today);

  switch (period) {
    case "custom":
      if (custom) return { startDay: custom.startDay, endDay: custom.endDay };
      return { startDay: weekStart, endDay: weekStart + 6 };
    case "overall":
      if (project) return { startDay: project.startDay, endDay: Math.max(project.startDay, Math.min(project.endDay, project.startDay + MAX_PERIOD_DAYS - 1)) };
      return { startDay: weekStart, endDay: weekStart + 6 };
    case "last_week":
      return { startDay: weekStart - 7, endDay: weekStart - 1 };
    case "next_week":
      return { startDay: weekStart + 7, endDay: weekStart + 13 };
    case "last_month": {
      const first = startOfMonth(addMonths(startOfMonth(today), -1));
      return { startDay: first, endDay: endOfMonth(first) };
    }
    case "this_month":
      return { startDay: startOfMonth(today), endDay: endOfMonth(today) };
    case "next_month": {
      const first = addMonths(startOfMonth(today), 1);
      return { startDay: first, endDay: endOfMonth(first) };
    }
    case "this_week":
    default:
      return { startDay: weekStart, endDay: weekStart + 6 };
  }
}
